import React, { useCallback, useEffect, useState } from 'react';

import ConfirmActionModal from 'adapter/in/common/components/ConfirmActionModal/ConfirmActionModal';

import i18n from 'asset/translation';
import { DeleteDashboardAnalysisCriteriaBuilder } from 'use-case/analysis/deleteDashboardAnalysis/deleteDashboardAnalysisCriteria';
import { isSome } from 'fp-ts/lib/Option';
import { dispatchReportLayoutsData } from 'adapter/in/reports/contexts/ReportLayoutDataContext';
import { useReportTemplateDependencies } from 'adapter/in/reports/reportsDependencies';
import { ReadLayoutsCriteriaBuilder } from 'use-case/layout/read/readLayoutsCriteria';
import gtm from 'adapter/in/reports/config/gtm';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { useUseCaseAlerts } from 'adapter/in/common/hooks/useUseCaseAlerts';

const useOnDeleteReportAnalysisModal = (dashboardId: string, analysisId: string) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { deleteReportAnalysis } = useReportTemplateDependencies();
    const { data, execute: remove, isLoading, error, reset } = useCase(deleteReportAnalysis);
    const { getLayouts } = dispatchReportLayoutsData();

    useUseCaseAlerts(data, error, {
        successMessage: 'reports.reportAnalysis.deleteSuccess',
        errorMessage: 'reports.reportAnalysis.deleteError',
    });

    const handleOnDelete = () => {
        setShowDeleteModal(true);
    };

    const handleDelete = useCallback(
        () => remove(DeleteDashboardAnalysisCriteriaBuilder.create().withAnalysisId(analysisId).withDashboardId(dashboardId).build()),
        []
    );
    useEffect(() => {
        if (isSome(data)) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(dashboardId).build());
            reset();
            setShowDeleteModal(false);
        }
    }, [data]);

    const onDeleteModal = (
        <ConfirmActionModal
            isOpen={showDeleteModal}
            onClose={() => {
                setShowDeleteModal(false);
                reset();
            }}
            mainButton={() => {
                handleDelete();
            }}
            message={i18n.t('widgets.delete.warningMsg')}
            title={i18n.t('widgets.actionDelete')}
            mainButtonText={i18n.t('widgets.delete.yes')}
            mainButtonVariant='destructive'
            hasError={isSome(error)}
            isLoading={isLoading}
            dataIdContext={{
                main: gtm.deleteReportAnalysis.delete,
                cancel: gtm.deleteReportAnalysis.cancel,
                tryAgain: gtm.deleteReportAnalysis.tryAgain,
            }}
        />
    );

    return { handleOnDelete, onDeleteModal };
};

export default useOnDeleteReportAnalysisModal;
